import { useTranslation } from 'react-i18next';

import { getFullName } from 'common/utils/getFullName';
import getProductTypeLabel from '@/components/Assets/helpers/getProductTypeLabel';
import getProductTypeIcon from '@/components/Assets/helpers/getProductTypeIcon';
import { Asset } from '@/types/assets';
import {
  Button,
  ColorsV2,
  Flex,
  Icon,
  IconInCircle,
  PaneWithBorder,
  Text,
  spaces,
} from '@electricjs/arc';

type DeviceCardProps = {
  asset: Asset;
  // Only the retrieval step lets the admin fill in a missing serial number, so
  // the warning banner is tied to this handler being provided: the other steps
  // would otherwise render a call to action that does nothing.
  onAddSerialNumber?: () => void;
};

const DeviceCard = ({ asset, onAddSerialNumber }: DeviceCardProps) => {
  const { t } = useTranslation('common');

  const hardwareModel = asset.device?.hardwareModel || asset.model;
  const deviceName =
    asset.name || hardwareModel || t('offboardings.deviceCard.unnamedDevice');
  const assigneeName = getFullName(
    asset.assignedToEmployee?.firstName,
    asset.assignedToEmployee?.lastName
  );

  const details = [
    {
      key: 'type',
      label: t('offboardings.deviceCard.labels.type'),
      value: getProductTypeLabel(asset.productType.name),
    },
    {
      key: 'serial-number',
      label: t('offboardings.deviceCard.labels.serialNumber'),
      value: asset.serialNumber,
    },
    {
      key: 'assignee',
      label: t('offboardings.deviceCard.labels.assignee'),
      value: assigneeName,
    },
    {
      key: 'site',
      label: t('offboardings.deviceCard.labels.site'),
      value: asset.site?.name,
    },
  ].filter(detail => !!detail.value);

  return (
    <PaneWithBorder
      id="offboarding-device-card"
      // PaneWithBorder only forwards `id` to the DOM when it's interactive
      // (has an `onClick`), which this card isn't — pass `testId` explicitly
      // so it remains reliably queryable in tests.
      testId="offboarding-device-card"
      stack
      rowGap={spaces.space_s}
      p={spaces.space_s}
      width="100%"
      backgroundColor={ColorsV2.BACKGROUND_CONTENT}>
      <Flex alignItems="center" width="100%">
        <IconInCircle
          icon={getProductTypeIcon(asset.productType.name)}
          iconSize="medium"
          size="3.6rem"
          borderRadius="8px"
          flexShrink={0}
          color={ColorsV2.PRIMARY}
          background={ColorsV2.PRIMARY_LIGHTEST}
        />
        <Flex
          vertical
          rowGap={spaces.space_2xs}
          ml={spaces.space_s}
          flexGrow={1}
          minWidth={0}>
          <Text
            variant="label-large"
            display="block"
            width="100%"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            testId="offboarding-device-card-name">
            {deviceName}
          </Text>
          <Flex wrap columnGap={spaces.space_m} rowGap={spaces.space_2xs}>
            {details.map(detail => (
              <Text
                key={detail.key}
                variant="body-2"
                intent="secondary"
                testId={`offboarding-device-card-${detail.key}`}>
                {`${detail.label} ${detail.value}`}
              </Text>
            ))}
          </Flex>
        </Flex>
      </Flex>
      {!asset.serialNumber && onAddSerialNumber && (
        <Flex
          id="offboarding-device-card-missing-serial-banner"
          testId="offboarding-device-card-missing-serial-banner"
          alignItems="flex-end"
          justifyContent="space-between"
          width="100%"
          p={spaces.space_s}
          borderRadius="8px"
          backgroundColor={ColorsV2.WARNING_LIGHTEST}>
          <Flex alignItems="center" flexShrink={1} minWidth={0}>
            <Icon icon="alert-triangle" intent="warning" />
            <Text variant="body-2" ml={spaces.space_xs}>
              {t('offboardings.deviceCard.missingSerialBanner.message')}
            </Text>
          </Flex>
          <Button
            id="offboarding-device-card-add-serial-number-button"
            testId="offboarding-device-card-add-serial-number-button"
            variant="link"
            flexShrink={0}
            ml={spaces.space_s}
            onClick={onAddSerialNumber}>
            <span style={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              {t('offboardings.deviceCard.missingSerialBanner.addSerialNumber')}
            </span>
          </Button>
        </Flex>
      )}
    </PaneWithBorder>
  );
};

export default DeviceCard;
